import { ChangeEvent, FC, useEffect, useRef, useState } from "react";

import type { MealInsightHistoryResponse } from "@9amhealth/openapi";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { useMediaQuery } from "@react-hooks-library/core";
import { translate } from "lib/blocs/TranslateionBloc";
import { useNavigate } from "react-router-dom";
import {
  APP_BREAKPOINT,
  APP_CONTENT_WIDTH,
  APP_CONTENT_WIDTH_WITHOUT_PADDING,
  TOP_NAV_HEIGHT
} from "src/constants/layout";
import { IconPlus } from "src/ui/assets/icons/IconPlus";
import { intakeFile } from "src/lib/fileIntake/fileIntake";
import { isAndroidDevice } from "src/lib/platform";
import { getHeadingProps, useFocusHeading } from "src/lib/useFocusHeading";
import MealInsightsCubit from "src/state/MealInsightsCubit/MealInsightsCubit";
import Translate from "src/ui/components/Translate/Translate";

import MealInsightBloc from "./MealInsightBloc";
import MealInsightDayList from "./MealInsightDayList";
import MealInsightDialog from "./MealInsightDialog";
import MealInsightSkeleton from "./MealInsightSkeleton";
import MealInsightsDesktopPromo from "./MealInsightsDesktopPromo";
import MealInsightsFtuxCard from "./MealInsightsFtuxCard";
import RxDietCard from "./RxDietCard";

const Page = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--color-cream, #fffcf3);
  padding-top: var(--app-top, ${TOP_NAV_HEIGHT}px);
`;

const Content = styled.div`
  width: min(100%, ${APP_CONTENT_WIDTH}px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem 1.5rem 2rem;

  &[data-fill="true"] {
    flex: 1;
  }
`;

const Title = styled.h1`
  margin: 0;
  color: var(--color-charcoal, #212121);
  font-family: "STK Bureau Serif", serif;
  font-size: 2.25rem;
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: -0.54px;
`;

const DesktopContent = styled.div`
  width: ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.5rem;
  padding: 2.5rem 0 5rem;
`;

const Fab = styled.button`
  position: fixed;
  right: 1.5rem;
  bottom: calc(
    var(--safe-area-inset-bottom, 0px) + var(--navbar-padding-bottom, 55px) +
      1rem
  );
  z-index: 5;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    bottom: 1.5rem;
  }

  width: 3.375rem;
  height: 3.375rem;
  border: none;
  border-radius: 200px;
  background: var(--color-charcoal, #212121);
  color: var(--color-white, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const SKELETON_COUNT = 3;

const MealInsights: FC = () => {
  const [{ hasAnyMeals, initialLoadComplete }, { loadInitial }] =
    useBlocNext(MealInsightsCubit);
  const [{ loading: uploading }, { handleUploadPhoto }] =
    useBlocNext(MealInsightBloc);
  const navigate = useNavigate();
  const { headingRef } = useFocusHeading();
  const isDesktop = useMediaQuery(`(min-width: ${APP_BREAKPOINT}px)`);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [selectedMeal, setSelectedMeal] =
    useState<MealInsightHistoryResponse | null>(null);

  useEffect(() => {
    if (!initialLoadComplete) {
      void loadInitial();
    }
  }, [initialLoadComplete, loadInitial]);

  useEffect(() => {
    if (initialLoadComplete) {
      return;
    }
    const retryOnReconnect = (): void => void loadInitial();
    window.addEventListener("online", retryOnReconnect);
    return () => window.removeEventListener("online", retryOnReconnect);
  }, [initialLoadComplete, loadInitial]);

  const isLoading = !initialLoadComplete;
  const isFirstTime = initialLoadComplete && !hasAnyMeals;

  const openUploadPicker = (): void => uploadInputRef.current?.click();

  if (isFirstTime && isDesktop) {
    return (
      <Page>
        <h1 className="only-sr" ref={headingRef} {...getHeadingProps()}>
          <Translate msg="mealInsights.meals.title" />
        </h1>
        <DesktopContent>
          <MealInsightsDesktopPromo />
          <RxDietCard />
        </DesktopContent>
      </Page>
    );
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const rawFile = event.target.files?.[0];
    if (!rawFile) {
      return;
    }
    void intakeFile(rawFile).then((file) => {
      void handleUploadPhoto(file, navigate, "meals");
    });
  };

  return (
    <Page>
      <Content data-fill={isFirstTime}>
        {isFirstTime ? (
          <>
            <h1 className="only-sr" ref={headingRef} {...getHeadingProps()}>
              <Translate msg="mealInsights.meals.title" />
            </h1>
            <MealInsightsFtuxCard
              onUploadClick={openUploadPicker}
              disabled={uploading}
            />
            <RxDietCard />
          </>
        ) : (
          <>
            <Title ref={headingRef} {...getHeadingProps()}>
              <Translate msg="mealInsights.meals.title" />
            </Title>
            <RxDietCard />

            {isLoading ? (
              <MealInsightSkeleton count={SKELETON_COUNT} />
            ) : (
              <MealInsightDayList onMealClick={setSelectedMeal} />
            )}
          </>
        )}
      </Content>

      <input
        type="file"
        accept="image/png, image/jpeg, image/jpg"
        hidden
        capture={isAndroidDevice() ? "environment" : undefined}
        onChange={handleFileChange}
        ref={uploadInputRef}
        disabled={uploading}
      />

      {!isFirstTime && !isDesktop && (
        <Fab
          type="button"
          onClick={openUploadPicker}
          disabled={uploading}
          aria-label={translate("mealInsights")}
        >
          <IconPlus />
        </Fab>
      )}

      {selectedMeal && (
        <MealInsightDialog
          meal={selectedMeal}
          onClose={() => setSelectedMeal(null)}
        />
      )}
    </Page>
  );
};

export default MealInsights;
